/* eslint-disable react/no-unstable-nested-components */

/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticonRounded';
import InsertLinkIcon from '@mui/icons-material/InsertLinkRounded';
import InsertPhotoIcon from '@mui/icons-material/InsertPhotoRounded';
import InsertTableIcon from '@mui/icons-material/TableViewRounded';

import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import TextFormatIcon from '@mui/icons-material/TextFormatRounded';

import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import { Box, Divider } from '@mui/material';

import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $isListNode,
  ListNode,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import { $getSelectionStyleValueForProperty, $patchStyleText } from '@lexical/selection';
import { $isTableNode } from '@lexical/table';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_NORMAL,
  KEY_MODIFIER_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useState } from 'react';
import useModal from '../../hooks/useModal';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { sanitizeUrl } from '../../utils/url';
import { INSERT_COLLAPSIBLE_COMMAND } from '../CollapsiblePlugin';
import {
  InsertImageDialog,
} from '../ImagesPlugin';
import { InsertTableDialog } from '../TablePlugin';
import ColorPickerButton from './Components/common/ColorPickerButton';
import StyledBox, { StyledButton } from './styles';
import UndoButton from './Components/UndoButton';
import RedoButton from './Components/RedoButton';
import FormatBoldButton from './Components/FormatBoldButton';
import FormatItalicButton from './Components/FormatItalicButton';
import FormatUnderlineButton from './Components/FormatUnderlineButton';
import FormatListUnorderedButton from './Components/FormatListUnorderedButton';
import FormatListOrderedButton from './Components/FormatListOrderedButton';
import IndentIncreaseButton from './Components/IndentIncreaseButton';
import IndentDecreaseButton from './Components/IndentDecreaseButton';
import FormatAlignLeftButton from './Components/FormatAlignLeftButton';
import FormatAlignJustifyButton from './Components/FormatAlignJustifyButton';
import FormatAlignRightButton from './Components/FormatAlignRightButton';
import FormatAlignCenterButton from './Components/FormatAlignCenterButton';

const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
};

const rootTypeToRootName = {
  root: 'Root',
  table: 'Table',
};

export default function ToolbarPlugin(): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>('paragraph');
  const [rootType, setRootType] = useState<keyof typeof rootTypeToRootName>('root');
  const [fontColor, setFontColor] = useState<string>('#000');
  const [bgColor, setBgColor] = useState<string>('#fff');
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [modal, showModal] = useModal();
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element = anchorNode.getKey() === 'root'
        ? anchorNode
        : $findMatchingParent(anchorNode, (e) => {
          const parent = e.getParent();
          return parent !== null && $isRootOrShadowRoot(parent);
        });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      const tableNode = $findMatchingParent(node, $isTableNode);
      if ($isTableNode(tableNode)) {
        setRootType('table');
      } else {
        setRootType('root');
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode,
          );
          const type = parentList
            ? parentList.getListType()
            : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          if (type in blockTypeToBlockName) {
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
        }
      }
      // Handle buttons
      setFontColor(
        $getSelectionStyleValueForProperty(selection, 'color', '#000'),
      );
      setBgColor(
        $getSelectionStyleValueForProperty(
          selection,
          'background-color',
          '#fff',
        ),
      );
    }
  }, [activeEditor]);

  useEffect(() => editor.registerCommand(
    SELECTION_CHANGE_COMMAND,
    (_payload, newEditor) => {
      $updateToolbar();
      setActiveEditor(newEditor);
      return false;
    },
    COMMAND_PRIORITY_CRITICAL,
  ), [editor, $updateToolbar]);

  useEffect(() => mergeRegister(
    editor.registerEditableListener((editable) => {
      setIsEditable(editable);
    }),
    activeEditor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        $updateToolbar();
      });
    }),
    activeEditor.registerCommand<boolean>(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    ),
    activeEditor.registerCommand<boolean>(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    ),
  ), [$updateToolbar, activeEditor, editor]);

  useEffect(() => activeEditor.registerCommand(
    KEY_MODIFIER_COMMAND,
    (payload) => {
      const event: KeyboardEvent = payload;
      const { code, ctrlKey, metaKey } = event;

      if (code === 'KeyK' && (ctrlKey || metaKey)) {
        event.preventDefault();
        return activeEditor.dispatchCommand(
          TOGGLE_LINK_COMMAND,
          sanitizeUrl('https://'),
        );
      }
      return false;
    },
    COMMAND_PRIORITY_NORMAL,
  ), [activeEditor, isLink]);

  const applyStyleText = useCallback(
    (styles: Record<string, string>) => {
      activeEditor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, styles);
        }
      });
    },
    [activeEditor],
  );

  const onFontColorSelect = useCallback(
    (value: string) => {
      applyStyleText({ color: value });
    },
    [applyStyleText],
  );

  const onBgColorSelect = useCallback(
    (value: string) => {
      applyStyleText({ 'background-color': value });
    },
    [applyStyleText],
  );

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl('https://'));
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  return (
    <StyledBox className="toolbar">
      <Box className="subcategory">
        <UndoButton canUndo={canUndo} isEditable={isEditable} activeEditor={activeEditor} />
        <RedoButton canRedo={canRedo} isEditable={isEditable} activeEditor={activeEditor} />
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box className="subcategory">
        <FormatBoldButton isBold={isBold} isEditable={isEditable} activeEditor={activeEditor} />
        <FormatItalicButton isItalic={isItalic} isEditable={isEditable} activeEditor={activeEditor} />
        <FormatUnderlineButton isUnderline={isUnderline} isEditable={isEditable} activeEditor={activeEditor} />
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box className="subcategory">
        <FormatListUnorderedButton isEditable={isEditable} blockType={blockType} editor={editor} />
        <FormatListOrderedButton isEditable={isEditable} blockType={blockType} editor={editor} />
        <IndentIncreaseButton isEditable={isEditable} blockType={blockType} activeEditor={activeEditor} />
        <IndentDecreaseButton isEditable={isEditable} blockType={blockType} activeEditor={activeEditor} />
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box className="subcategory">
        <FormatAlignLeftButton isEditable={isEditable} blockType={blockType} activeEditor={activeEditor} />
        <FormatAlignCenterButton isEditable={isEditable} blockType={blockType} activeEditor={activeEditor} />
        <FormatAlignRightButton isEditable={isEditable} blockType={blockType} activeEditor={activeEditor} />
        <FormatAlignJustifyButton isEditable={isEditable} blockType={blockType} activeEditor={activeEditor} />
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box className="subcategory">
        <StyledButton
          disabled={!isEditable}
          onClick={insertLink}
          className={`toolbar-item spaced ${isLink ? 'active' : ''}`}
          aria-label="Insert link"
          title="Insert link"
          type="button"
        >
          <InsertLinkIcon />
        </StyledButton>
        <StyledButton
          onClick={() => {
            showModal('Insert Image', (onClose: () => void) => (
              <InsertImageDialog
                activeEditor={activeEditor}
                onClose={onClose}
              />
            ));
          }}
          className="item"
        >
          <InsertPhotoIcon />
        </StyledButton>
        <StyledButton
          onClick={() => {
            if (rootType === 'table') {
              // TODO: change behavior is table is currently selected
            } else {
              showModal('Insert Table', (onClose: () => void) => (
                <InsertTableDialog
                  activeEditor={activeEditor}
                  onClose={onClose}
                />
              ));
            }
          }}
          className={`toolbar-item spaced ${rootType === 'table' ? 'active' : ''}`}
        >
          <InsertTableIcon />
        </StyledButton>
        <StyledButton
          disabled
        >
          <InsertEmoticonIcon />
        </StyledButton>
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box className="subcategory">
        <ColorPickerButton
          disabled={!isEditable}
          className="toolbar-item color-picker"
          aria-label="Formatting text color"
          color={fontColor}
          onChange={onFontColorSelect}
          title="text color"
        >
          <TextFormatIcon />
        </ColorPickerButton>
        <ColorPickerButton
          disabled={!isEditable}
          className="toolbar-item color-picker"
          aria-label="Formatting background color"
          color={bgColor}
          onChange={onBgColorSelect}
          title="bg color"
        >
          <FormatColorFillIcon />
        </ColorPickerButton>
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box className="subcategory">
        <StyledButton
          onClick={() => {
            editor.dispatchCommand(INSERT_COLLAPSIBLE_COMMAND, undefined);
          }}
          className="item"
        >
          <UnfoldMoreIcon />
        </StyledButton>

      </Box>
      {modal}
    </StyledBox>
  );
}
